// Food pellets (architecture §9.2 step 4, mechanics §5.1, art-assets §5, visuals §6), culled:
// - classic: plain filled circles, no outline, in the agar-like generated colour of the pellet id
//   (visuals §2), drawn at agar's size (server r 10 → 15 u, 22.4 → 25 u; collision unchanged) and
//   with no pop-in. Live pellets are batched into one path per colour bucket (the generated
//   colours quantised to 60 buckets: 6 channel orders × 10 steps of the free channel);
// - sketch: the palette colour `palette[hash32(id) mod n]`, the store's radius and pop-in, plus a
//   30 % lighter highlight dot;
// - pellets in their eaten effect are drawn after the live ones, one by one, at α = 1 − t and
//   r × (1 − 0.3 t) (visuals §9.1);
// - pellet art (`world/pellets/pellet-NN.png`, at Medium and above): the variant picked from the
//   id, multiply-tinted with the pellet colour (cached per image and colour), side = 2.5 × r.
// "Plain colors" draws every pellet in the plain grey.

import {
  generatedColor,
  hash32,
  mixRgb,
  pelletColor,
  pelletColorIndex,
  type Rgb,
} from '@ballio/shared';
import type { AssetImage } from '../assets/loader.js';
import { ENTITY_EFFECT, type EntityRecord } from '../game/world-store.js';
import { circleInRect } from './cull.js';
import { baseRadius, displayAlpha, effectRadius, pelletDrawRadius } from './display.js';
import { type Frame, useWorldTransform } from './frame.js';
import { traceCircle } from './outline.js';
import type { SpriteCache } from './sprite-cache.js';
import { css } from './theme.js';

/** Image side = k × r for pellet art (art-assets §5). */
export const PELLET_ART_K = 2.5;

/** Pellets smaller than this on screen (CSS px radius) are skipped. */
export const MIN_FOOD_RADIUS_PX = 0.3;

/** Steps of the free channel per channel order in the classic colour buckets. */
export const FOOD_FREE_STEPS = 10;
/** Classic colour buckets: 6 orders × FOOD_FREE_STEPS. */
export const FOOD_BUCKETS = 6 * FOOD_FREE_STEPS;

/** The user's pellet variants and whether each is multiply-tinted. */
export interface FoodArt {
  readonly images: readonly AssetImage[];
  readonly tint: readonly boolean[];
}

/** The pellet art variant index for a pellet id (independent of its colour index). */
export function pelletVariant(id: number, variants: number): number {
  return variants > 0 ? (hash32(id) >>> 16) % variants : 0;
}

/** The classic colour bucket (0–59) of a pellet id. */
export function pelletBucket(id: number): number {
  const h = hash32(id);
  const order = h % 6;
  const free = (h >>> 8) & 0xff;
  return (
    order * FOOD_FREE_STEPS +
    Math.min(FOOD_FREE_STEPS - 1, Math.floor((free * FOOD_FREE_STEPS) / 256))
  );
}

/** The colour a classic bucket is drawn in (the centre of its free-channel step). */
export function pelletBucketColor(bucket: number): Rgb {
  const order = Math.floor(bucket / FOOD_FREE_STEPS);
  const step = bucket % FOOD_FREE_STEPS;
  return generatedColor(order, Math.round(((step + 0.5) * 256) / FOOD_FREE_STEPS));
}

/**
 * A pellet's drawn radius: classic maps the server radius to agar's 15–25 u and ignores the
 * pop-in; the eat effect shrinks it to × (1 − 0.3 t). Sketch keeps the store's radius.
 */
export function foodDrawRadius(rec: EntityRecord, classic: boolean): number {
  if (!classic) return rec.r;
  const base = pelletDrawRadius(baseRadius(rec, true));
  return effectRadius(rec, base);
}

export class FoodPainter {
  private readonly buckets: EntityRecord[][] = [];
  private readonly eaten: EntityRecord[] = [];

  draw(f: Frame, food: readonly EntityRecord[], art: FoodArt | null, sprites: SpriteCache): void {
    useWorldTransform(f);
    f.ctx.globalAlpha = 1;
    if (art && art.images.length > 0 && f.quality.foodSprites)
      this.drawSprites(f, food, art, sprites);
    else this.drawCircles(f, food);
    f.ctx.globalAlpha = 1;
  }

  /** The exact colour of one pellet (sprites, eaten pellets). */
  colorFor(f: Frame, id: number): Rgb {
    if (f.settings.plainColors) return f.theme.plain;
    const palette = f.theme.palette;
    return palette
      ? (palette[pelletColorIndex(id, palette.length)] ?? f.theme.plain)
      : pelletColor(id);
  }

  private drawCircles(f: Frame, food: readonly EntityRecord[]): void {
    const { ctx, view } = f;
    const plain = f.settings.plainColors;
    const palette = f.theme.palette;
    const classic = f.theme.style !== 'sketch';
    const n = plain ? 1 : palette ? Math.max(1, palette.length) : FOOD_BUCKETS;
    while (this.buckets.length < n) this.buckets.push([]);
    for (let i = 0; i < n; i++) (this.buckets[i] as EntityRecord[]).length = 0;
    const eaten = this.eaten;
    eaten.length = 0;
    const minR = MIN_FOOD_RADIUS_PX / f.zoom;
    let count = 0;
    for (let i = 0; i < food.length; i++) {
      const rec = food[i] as EntityRecord;
      const r = foodDrawRadius(rec, classic);
      if (!(r > minR) || !circleInRect(rec.x, rec.y, r, view)) continue;
      count++;
      if (rec.effect === ENTITY_EFFECT.EATEN) {
        eaten.push(rec);
        continue;
      }
      const idx = plain ? 0 : palette ? pelletColorIndex(rec.id, n) : pelletBucket(rec.id);
      (this.buckets[idx] as EntityRecord[]).push(rec);
    }
    f.stats.food += count;
    if (count === 0) return;
    const sketch = !classic;
    for (let b = 0; b < n; b++) {
      const list = this.buckets[b] as EntityRecord[];
      if (list.length === 0) continue;
      const color = plain
        ? f.theme.plain
        : palette
          ? (palette[b] ?? f.theme.plain)
          : pelletBucketColor(b);
      ctx.fillStyle = css(color);
      ctx.beginPath();
      for (let i = 0; i < list.length; i++) {
        const rec = list[i] as EntityRecord;
        traceCircle(ctx, rec.x, rec.y, foodDrawRadius(rec, classic));
      }
      ctx.fill();
      if (sketch) this.highlights(f, list, color);
      list.length = 0;
    }
    // Eaten pellets fade one by one (visuals §9.1).
    for (let i = 0; i < eaten.length; i++) {
      const rec = eaten[i] as EntityRecord;
      const a = displayAlpha(rec);
      if (!(a > 0.004)) continue;
      ctx.globalAlpha = a;
      ctx.fillStyle = css(this.colorFor(f, rec.id));
      ctx.beginPath();
      traceCircle(ctx, rec.x, rec.y, foodDrawRadius(rec, classic));
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    eaten.length = 0;
  }

  /** The sketch placeholder's highlight dot (art-assets §5): 30 % lighter, up and left. */
  private highlights(f: Frame, list: readonly EntityRecord[], color: Rgb): void {
    const { ctx } = f;
    const minHighlightR = 2.5 / f.zoom;
    ctx.fillStyle = css(mixRgb(color, 0xffffff, 0.3));
    ctx.beginPath();
    let any = false;
    for (let i = 0; i < list.length; i++) {
      const rec = list[i] as EntityRecord;
      if (rec.r < minHighlightR) continue;
      traceCircle(ctx, rec.x - rec.r * 0.32, rec.y - rec.r * 0.32, rec.r * 0.3);
      any = true;
    }
    if (any) ctx.fill();
  }

  private drawSprites(
    f: Frame,
    food: readonly EntityRecord[],
    art: FoodArt,
    sprites: SpriteCache,
  ): void {
    const { ctx, view } = f;
    const minR = MIN_FOOD_RADIUS_PX / f.zoom;
    const variants = art.images.length;
    const classic = f.theme.style !== 'sketch';
    let count = 0;
    for (let i = 0; i < food.length; i++) {
      const rec = food[i] as EntityRecord;
      const r = foodDrawRadius(rec, classic);
      const half = r * (PELLET_ART_K / 2);
      if (!(r > minR) || !circleInRect(rec.x, rec.y, half, view)) continue;
      const v = pelletVariant(rec.id, variants);
      const src = art.images[v];
      const color = this.colorFor(f, rec.id);
      const img = src ? (art.tint[v] !== false ? sprites.tinted(src, color) : src) : null;
      ctx.globalAlpha = displayAlpha(rec);
      if (img) {
        ctx.drawImage(img, rec.x - half, rec.y - half, half * 2, half * 2);
      } else {
        ctx.fillStyle = css(color);
        ctx.beginPath();
        traceCircle(ctx, rec.x, rec.y, r);
        ctx.fill();
      }
      count++;
    }
    ctx.globalAlpha = 1;
    f.stats.food += count;
  }
}
